import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { useDictation } from './useDictation';
import Waveform from './Waveform';
import InstructionReview from './InstructionReview';
import InstructionAssistantModalShell from './InstructionAssistantModalShell';

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const FEEDBACK_DRAFT_KEY = 'instruction-assistant-feedback-draft';

function readFeedbackDraft() {
  try {
    return localStorage.getItem(FEEDBACK_DRAFT_KEY) || '';
  } catch {
    return '';
  }
}

function writeFeedbackDraft(value) {
  try {
    if (value) {
      localStorage.setItem(FEEDBACK_DRAFT_KEY, value);
    } else {
      localStorage.removeItem(FEEDBACK_DRAFT_KEY);
    }
  } catch {
    // localStorage may be unavailable; the in-memory input still works.
  }
}

/**
 * Instruction Assistant — a modal that turns a clinician's plain-language feedback
 * about the AI's note into revised template instructions and examples, summarized as one
 * line per changed section that the clinician can keep or discard before applying.
 *
 * Steps: capture -> planning -> review. The modal owns the shared state.
 * Dictation never shows the raw transcript: finishing a recording goes straight to
 * `planning` and then to `review`.
 */
const InstructionAssistantModal = ({
  isOpen,
  onClose,
  apiClient,
  configId,
  getSectionMetadata,
  onSaveToTemplate,
  onSaveToTemplateAndNote,
  visitTranscript = '',
  compact = false,
  initialFeedback = '',
}) => {
  const [step, setStep] = useState('capture'); // 'capture' | 'planning' | 'review'
  const [feedback, setFeedback] = useState('');
  const [recSeconds, setRecSeconds] = useState(0);
  // plan mirrors the planInstructionChanges request:
  // { status: 'idle' | 'done' | 'error', changes, error }.
  const [plan, setPlan] = useState({
    status: 'idle',
    changes: [],
    error: null,
  });
  const feedbackContextRef = useRef({
    transcript: '',
    source: null,
    visitTranscript: '',
  });
  const [saving, setSaving] = useState(false);

  const textareaRef = useRef(null);
  // Monotonic token so a superseded or late response can never commit state — the
  // only "cancel" we need, since the API layer exposes no network abort signal.
  const requestIdRef = useRef(0);
  const wasOpenRef = useRef(false);

  // Plan the changes the clinician's free-text feedback implies — one bullet per
  // distinct change with the sections it touches. No revisions are written yet;
  // that happens on Apply. Used by both the typed "Next" and a finished dictation.
  const runPlan = async (rawText, source = 'typed') => {
    const text = (rawText || '').trim();
    if (!text) {
      toast.info("Didn't catch that — please try again.");
      setStep('capture');
      return;
    }
    feedbackContextRef.current = {
      transcript: text,
      source,
      visitTranscript: visitTranscript || '',
    };
    setStep('planning');
    const reqId = requestIdRef.current + 1;
    requestIdRef.current = reqId;
    const startedAt = Date.now();
    try {
      let sectionMetadata = null;
      let planningConfigId = configId;
      if (getSectionMetadata) {
        try {
          const metadataResult = await getSectionMetadata();
          if (
            metadataResult &&
            typeof metadataResult === 'object' &&
            !Array.isArray(metadataResult) &&
            Object.prototype.hasOwnProperty.call(
              metadataResult,
              'sectionMetadata',
            )
          ) {
            sectionMetadata = metadataResult.sectionMetadata;
            planningConfigId = metadataResult.configId || planningConfigId;
          } else {
            sectionMetadata = metadataResult;
          }
        } catch (err) {
          console.warn(
            'Could not load instruction assistant section metadata:',
            err,
          );
          setPlan({
            status: 'error',
            changes: [],
            error:
              err?.message ||
              'Could not scan the current note. Please try again.',
          });
          setStep('review');
          return;
        }
        if (requestIdRef.current !== reqId) {
          return;
        }
      }
      const rsp = await apiClient.planInstructionChanges(
        planningConfigId,
        text,
        sectionMetadata,
      );
      // Keep the processing state on screen long enough for the user to read (no flicker)
      const elapsed = Date.now() - startedAt;
      if (elapsed < 800) {
        await new Promise((resolve) => setTimeout(resolve, 800 - elapsed));
      }
      if (requestIdRef.current !== reqId) {
        return; // superseded by a newer request — discard
      }
      if (rsp && rsp.error) {
        setPlan({
          status: 'error',
          changes: [],
          error:
            typeof rsp.error === 'string'
              ? rsp.error
              : 'Could not work out the changes.',
        });
        setStep('review');
        return;
      }
      setPlan({
        status: 'done',
        changes: (rsp && rsp.changes) || [],
        error: null,
      });
      setStep('review');
    } catch (err) {
      if (requestIdRef.current !== reqId) {
        return;
      }
      setPlan({
        status: 'error',
        changes: [],
        error: 'Something went wrong. Please try again.',
      });
      setStep('review');
    }
  };

  // A finished recording lands here with the transcript (empty on failure). We
  // plan from it directly — the raw transcript is never shown to the user.
  const { isRecording, mediaStream, toggle, stop, cancel } = useDictation(
    apiClient,
    (transcript) => runPlan(transcript, 'dictation'),
  );

  const handleClose = useCallback(() => {
    cancel();
    onClose();
  }, [cancel, onClose]);

  // Reset to a fresh capture step each time the modal opens; discard any
  // in-progress recording on close.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened) {
      const requestedFeedback = initialFeedback.trim();
      setStep('capture');
      setFeedback(requestedFeedback || readFeedbackDraft());
      // Invalidate any in-flight request so a late response from a previous
      // session can't populate this one.
      requestIdRef.current += 1;
      setPlan({
        status: 'idle',
        changes: [],
        error: null,
      });
      feedbackContextRef.current = {
        transcript: '',
        source: null,
        visitTranscript: visitTranscript || '',
      };
      setSaving(false);
      if (requestedFeedback) {
        runPlan(requestedFeedback, 'note_feedback');
      }
    } else if (!isOpen) {
      requestIdRef.current += 1;
      cancel();
    }
  }, [isOpen, cancel, visitTranscript, initialFeedback]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus the textarea when the capture step's text mode is shown.
  useEffect(() => {
    if (isOpen && step === 'capture' && !isRecording && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, step, isRecording]);

  // Count up the elapsed recording time while recording.
  useEffect(() => {
    if (!isRecording) {
      setRecSeconds(0);
      return undefined;
    }
    setRecSeconds(0);
    const id = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  // Apply actions dismiss the modal immediately and run in the background; the
  // host owns any post-dismiss feedback (e.g. the note "Updating… / Updated"
  // strip). `keptChanges` is the subset of the plan the clinician kept in review.
  const handleSaveToTemplate = (keptChanges) => {
    if (!onSaveToTemplate) {
      toast.error('Saving is not available here.');
      return;
    }
    onClose();
    // Host owns success feedback (the EMR view shows a "Template updated" strip;
    // other surfaces toast). The modal only surfaces errors.
    Promise.resolve(onSaveToTemplate(keptChanges, feedbackContextRef.current))
      .then(() => writeFeedbackDraft(''))
      .catch(() =>
        toast.error('Could not save the preferences. Please try again.'),
      );
  };

  const handleSaveToTemplateAndNote = (keptChanges) => {
    if (!onSaveToTemplateAndNote) {
      return;
    }
    onClose();
    // No success toast here — the host shows the "Note updated" strip.
    Promise.resolve(
      onSaveToTemplateAndNote(keptChanges, feedbackContextRef.current),
    )
      .then(() => writeFeedbackDraft(''))
      .catch(() => toast.error('Could not update the note. Please try again.'));
  };

  const canSubmitTextFeedback = feedback.trim().length > 0;
  // "Update Note & Save Preference" shows only when the host provides the note-write
  // action (the EMR view). The web app and Note Settings omit it.
  const noteSaveReady = !!onSaveToTemplateAndNote;

  // Comprehend brand green, applied inline (not a Bootstrap theme color).
  const GREEN = '#1d9e75';
  const SOFT_CREAM_SURFACE = '#fbfaf7';
  const SOFT_CREAM_BORDER = '#e9e4da';
  const headerTitle =
    step === 'review' ? "Here's what I heard" : 'What would you like changed?';
  const modalFooter =
    step === 'capture' ? (
      isRecording ? (
        <div className="w-100">
          <Button
            type="button"
            color="success"
            block
            className="font-weight-bold py-2 shadow-sm"
            style={{ backgroundColor: GREEN, borderColor: GREEN, borderRadius: 12 }}
            onClick={() => {
              setStep('planning');
              stop();
            }}
          >
            Done speaking
          </Button>
          <div className="text-center mt-2">
            <Button
              type="button"
              color="link"
              size="sm"
              className="font-weight-bold"
              style={{ color: GREEN }}
              onClick={cancel}
            >
              Switch to typing
            </Button>
            <Button
              type="button"
              color="link"
              size="sm"
              className="text-muted"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="w-100">
          <div className="d-flex">
            <Button
              type="button"
              color="secondary"
              className="mr-2 d-flex align-items-center justify-content-center"
              style={{
                width: 54,
                borderRadius: 12,
                backgroundColor: SOFT_CREAM_SURFACE,
                borderColor: SOFT_CREAM_BORDER,
              }}
              onClick={toggle}
              aria-label="Dictate"
              title="Dictate"
            >
              <i className="fa fa-microphone" style={{ color: '#7a7a7a' }}></i>
            </Button>
            <Button
              type="button"
              color="success"
              className="flex-grow-1 d-flex align-items-center justify-content-center font-weight-bold py-2 shadow-sm"
              style={{ backgroundColor: GREEN, borderColor: GREEN, borderRadius: 12 }}
              onClick={() => runPlan(feedback)}
              disabled={!canSubmitTextFeedback}
            >
              Next
              <i className="fa fa-arrow-right ml-2"></i>
            </Button>
          </div>
          <div className="text-center mt-2">
            <Button
              type="button"
              color="link"
              size="sm"
              className="text-muted"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      )
    ) : null;

  return (
    <InstructionAssistantModalShell
      isOpen={isOpen}
      title={headerTitle}
      ariaLabel="Instruction Assistant"
      iconClassName="fa fa-wand-magic-sparkles"
      onClose={handleClose}
      footer={modalFooter}
      keepMounted
      compact={compact}
    >
      {step === 'capture' &&
        (isRecording ? (
          <div className="d-flex flex-column align-items-center justify-content-center text-center py-4">
            <Waveform stream={mediaStream} />
            <div className="d-flex align-items-center font-weight-bold text-dark mt-3">
              <i
                className="fa fa-circle text-danger mr-2"
                style={{ fontSize: 8 }}
                aria-hidden="true"
              />
              <span>{formatTime(recSeconds)}</span>
            </div>
            <div className="small text-muted mt-2">
              Speak naturally — tap Done when finished
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column">
            <div className="mb-3">
              <div
                className="text-muted font-italic small px-3 py-2 mb-2"
                style={{
                  backgroundColor: SOFT_CREAM_SURFACE,
                  border: `1px solid ${SOFT_CREAM_SURFACE}`,
                  borderRadius: 10,
                }}
              >
                "Stop adding goals the patient never mentioned"
              </div>
              <div
                className="text-muted font-italic small px-3 py-2 mb-2"
                style={{
                  backgroundColor: SOFT_CREAM_SURFACE,
                  border: `1px solid ${SOFT_CREAM_SURFACE}`,
                  borderRadius: 10,
                }}
              >
                "Keep the assessment to 2 sentences max"
              </div>
              <div
                className="text-muted font-italic small px-3 py-2"
                style={{
                  backgroundColor: SOFT_CREAM_SURFACE,
                  border: `1px solid ${SOFT_CREAM_SURFACE}`,
                  borderRadius: 10,
                }}
              >
                "Write the plan in bullet points"
              </div>
            </div>
            <Input
              type="textarea"
              id="ia-feedback"
              ref={textareaRef}
              rows="4"
              style={{
                backgroundColor: SOFT_CREAM_SURFACE,
                borderColor: SOFT_CREAM_BORDER,
                borderRadius: 12,
                padding: compact ? '11px 12px' : '13px 14px',
              }}
              value={feedback}
              onChange={(e) => {
                const nextFeedback = e.target.value;
                setFeedback(nextFeedback);
                writeFeedbackDraft(nextFeedback);
              }}
              placeholder="Type or use the mic to speak your changes…"
              aria-label="What would you like changed?"
            />
          </div>
        ))}

      {step === 'planning' && (
        <div className="d-flex flex-column align-items-center text-center py-5">
          <Spinner style={{ color: GREEN }} />
          <div
            className="font-weight-bold text-dark mt-4"
            style={{ fontSize: 20 }}
          >
            Taking a look…
          </div>
          <div className="text-muted mt-2 w-75" style={{ fontSize: 16 }}>
            One moment while I find what to change.
          </div>
        </div>
      )}

      {step === 'review' && (
        <InstructionReview
          status={plan.status}
          changes={plan.changes}
          error={plan.error}
          saving={saving}
          noteSaveReady={noteSaveReady}
          onSaveTemplate={handleSaveToTemplate}
          onSaveTemplateAndNote={handleSaveToTemplateAndNote}
          onReject={handleClose}
          onBack={() => setStep('capture')}
          onRetry={() => runPlan(feedback)}
        />
      )}
    </InstructionAssistantModalShell>
  );
};

export default InstructionAssistantModal;
